import { createSelector } from "@reduxjs/toolkit";
import { isEqual } from "es-toolkit";
import { get, set } from "es-toolkit/compat";
import type { Dispatch } from "react";

import {
  type DesignerNodeData,
  type GetNodeTypeIdById,
  type MultilingualizableConfigValue,
  type MultilingualNodeConfig,
  type StateAction,
  assert,
  dispatchParamSelector,
  enableMultilingualOnPropertyForLanguage,
  getMultilingualNodeConfigSelector,
  getNodeTypeIdSelector,
  isMultilingualProperty,
  updateNodeConfigAction,
} from "@skilitics/nautilus-core-designer-data";

import type { GetManifestByTypeId } from "../../types/index.ts";
import {
  type ValidateNodeConfig,
  getManifestSelector,
  languageCodesSelector,
  validateNodeConfigSelector,
} from "../contextualSelectors/index.ts";
import {
  type GetMultilingualOptInEnabled,
  getMultilingualOptInEnabledSelector,
} from "./multilingualOptInEnabledSelectors.ts";

export const setOptInPropertyMultilingualEnabledSelector = createSelector(
  [
    getManifestSelector,
    getNodeTypeIdSelector,
    getMultilingualOptInEnabledSelector,
    getMultilingualNodeConfigSelector,
    validateNodeConfigSelector,
    languageCodesSelector,
    dispatchParamSelector,
  ],
  (
    getManifest,
    getNodeTypeId,
    getMultilingualOptInEnabled,
    getConfigMultilingual,
    validateConfig,
    languageCodes,
    dispatch,
  ): OptInPropertyMultilingual =>
    setOptInPropertyMultilingualCreate({
      getManifest,
      getNodeTypeId,
      getConfigMultilingual,
      getMultilingualOptInEnabled,
      validateConfig,
      languageCodes,
      dispatch,
    }),
);

interface CreateOptions {
  getManifest: GetManifestByTypeId;
  getNodeTypeId: GetNodeTypeIdById;
  getMultilingualOptInEnabled: GetMultilingualOptInEnabled;
  getConfigMultilingual: (id: number) => DesignerNodeData["config"];
  validateConfig: ValidateNodeConfig;
  dispatch: Dispatch<StateAction>;
  languageCodes: {
    primary: string;
    active: string;
  };
}

type Value = { optIn: true; initialValue: MultilingualizableConfigValue } | { optIn: false };

export const setOptInPropertyMultilingualCreate =
  ({
    getManifest,
    getNodeTypeId,
    getMultilingualOptInEnabled,
    getConfigMultilingual,
    validateConfig,
    languageCodes,
    dispatch,
  }: CreateOptions) =>
  (id: number, masterProperty: string, value: Value) => {
    if (getMultilingualOptInEnabled(id, masterProperty, { lang: languageCodes.active }) === value.optIn) {
      return;
    }

    const config = getConfigMultilingual(id);

    const getPropertiesToSetFor = () => {
      const {
        designer: { multilingual },
      } = getManifest(getNodeTypeId(id));
      const settings = multilingual?.[masterProperty];
      assert(settings, "Not a multilingual property at all");
      assert(settings.optIn, "Not an opt-in multilingual property");
      if (settings.optIn === true) {
        return [masterProperty];
      }
      return [masterProperty, ...settings.optIn.dependents];
    };

    const getNewConfig = (): MultilingualNodeConfig => {
      const newConfig = structuredClone(config);
      if (value.optIn) {
        for (const property of getPropertiesToSetFor()) {
          set(
            newConfig,
            property,
            enableMultilingualOnPropertyForLanguage(
              get(config, property),
              languageCodes.active,
              // null effectively means "don't use the primary/fallback value. nust have nothing on this lang for now"
              property === masterProperty ? (value.initialValue ?? null) : null, // TODO: dependent properties can only have null for now. might need to add to api
              {
                primaryLang: languageCodes.primary,
              },
            ),
          );
        }
      } else {
        for (const property of getPropertiesToSetFor()) {
          const existingValue = get(newConfig, property);
          if (!isMultilingualProperty(existingValue)) {
            continue;
          }
          // should be === 2 but super defensive in production
          if (Object.keys(existingValue.__ml).length <= 2) {
            set(newConfig, property, existingValue.__ml[languageCodes.primary]);
          } else {
            // undefined effectively means "use the fallback/primary instead for this lang"
            set(newConfig, property, { __ml: { ...existingValue.__ml, [languageCodes.active]: undefined } });
          }
        }
      }
      return newConfig;
    };

    const newConfig = getNewConfig();
    validateConfig(id, newConfig);
    if (isEqual(config, newConfig)) {
      return;
    }

    dispatch(updateNodeConfigAction({ id, config: newConfig }));
  };

export type OptInPropertyMultilingual = ReturnType<typeof setOptInPropertyMultilingualCreate>;
